import React, { useState } from 'react';

// Icons
const ChatIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>;
const WidgetIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>;
const LibraryIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>;

const steps = [
    {
        icon: <div className="text-5xl">👋</div>,
        title: "Welcome to Dr.Rx!",
        text: "Your new AI-powered Pharmacy Co-Pilot. Let's take a quick 30-second tour of the key features to get you started.",
    },
    {
        icon: <ChatIcon />,
        title: "AI Co-Pilot Chat",
        text: "This is your central hub for asking complex clinical questions, summarizing guidelines, and getting instant, context-aware drug information. Access it anytime from the bottom navigation bar.",
    },
    {
        icon: <WidgetIcon />,
        title: "Customizable Dashboard",
        text: "Your Dashboard is made of widgets. Click 'Edit' to add, remove, and rearrange tools like Quick Actions and Clinical Alerts to fit your workflow.",
    },
    {
        icon: <LibraryIcon />,
        title: "The Clinical Library",
        text: "Explore the Library to find our full suite of 50+ specialized calculators, reference guides, and study tools. Everything you need is in one place.",
    },
    {
        icon: <div className="text-5xl">🚀</div>,
        title: "You're All Set!",
        text: "You're ready to dive in. Start by exploring your dashboard or ask the AI a question. Welcome aboard!",
    }
];

interface OnboardingTourProps {
    onComplete: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onComplete();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };
    
    const step = steps[currentStep];

    return (
        <div onClick={onComplete} className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50 modal-enter" role="dialog" aria-modal="true">
            <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-[#1a2a47] to-[#15243E] rounded-2xl border border-blue-900/50 w-full max-w-lg text-gray-300 p-8 shadow-2xl relative text-center">
                <div className="text-yellow-400 mb-6 flex justify-center">
                    {step.icon}
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">{step.title}</h2>
                <p className="text-gray-400 mb-8">{step.text}</p>
                
                <div className="flex items-center justify-center mb-6">
                    {steps.map((_, index) => (
                        <div key={index} className={`w-2 h-2 rounded-full mx-1 transition-all ${index === currentStep ? 'bg-yellow-400 scale-125' : 'bg-gray-600'}`} />
                    ))}
                </div>

                <div className="flex justify-between items-center">
                     <button 
                        onClick={handleBack}
                        className={`font-semibold text-gray-400 transition-opacity ${currentStep === 0 ? 'opacity-0' : 'opacity-100 hover:text-white'}`}
                        disabled={currentStep === 0}
                    >
                        Back
                    </button>
                    <button 
                        onClick={handleNext}
                        className="bg-yellow-400 text-gray-900 font-bold py-3 px-8 rounded-lg hover:bg-yellow-300 transition-transform hover:scale-105"
                    >
                        {currentStep === steps.length - 1 ? "Get Started" : "Next"}
                    </button>
                    <button 
                        onClick={onComplete}
                        className={`font-semibold text-gray-400 transition-opacity ${currentStep === steps.length - 1 ? 'opacity-0' : 'opacity-100 hover:text-white'}`}
                    >
                        Skip
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OnboardingTour;